import { observer } from "mobx-react";
import { FC, useEffect, useMemo, useState } from "react";
import { getFormValidation } from "src/Shared/Components";
import GridFormControl from "src/Shared/Components/GridFormControl/GridFormControl";
import ModalContent from "src/Shared/Components/ModalContent/ModalContent";
import { VIEW_MODE } from "src/Shared/Enums/ViewMode.enum";
import { IGridFormControlProps } from "src/Shared/Interfaces/IGridFormControlProps.interface";
import { fields } from "./fields";
import { useStore } from "src/Shared/Hooks/UseStore.hook";
import { ISalesDeal } from "../../Interfaces";
import { DealStatus, EDITOR_TYPES } from "src/Shared/Enums";
import { useModal } from "src/Shared/Hooks";
import SalesDealService from "../../Services/SalesDeal.service";
import { finalize, min } from "rxjs";
import { AxiosError } from "axios";
import { ToastService } from "src/Shared/Services";
import { IResult } from "src/Shared/Interfaces";
import { Box } from "@mui/material";
import AddNoteConfirmation from "src/Shared/Components/AddNoteConfirmation/AddNoteConfirmation";
import { request } from "http";

type Props = {
  mode: VIEW_MODE;
  saleDeal?: ISalesDeal;
  companyRowID?: string;
};

const AddOrEditSaleDeal: FC<Props> = ({ mode, saleDeal, companyRowID }) => {
  const {
    rootStore: {
      commonStore: { dealStatusList, loadCommonData },
      salesDealStore: { refreshSalesGridList },
      companyStore: { companyOverview, getCompanyOverview },
      uiStore,
    },
  } = useStore();

  const characterMaxLength = 800;
  const characterMinLength = 10;
  const modal = useModal();
  const updateSalesTotalValue = () => {
    getField('totalValue').set(
      Number(getField('valueOfHealthTickServices').value) +
        Number(getField('valueOfPolicyReview').value) +
        Number(getField('valueOfTraining').value) +
        Number(getField('valueOfTesting').value)
    );
    getField('dealStatusID').validate();
  };
  const orderValueRequiredForCertainStages = ({ field, form }) => {
    const importantStatuses = [
      DealStatus.FinalNegotiation,
      DealStatus.FinalApproval,
      DealStatus.ContractSigning,
      DealStatus.ClosedWonAndPostSaleFollowUp,
    ];

    const selectedStatus = parseInt(field.value, 10);
    const totalValue = parseFloat(form.$('totalValue').value || '0');

    const isValid =
      !importantStatuses.includes(selectedStatus) || totalValue > 0;

    return [
      isValid,
      'Please enter a value for at least one revenue field (Training, Testing, Policy Review, or Health Tick) before progressing to this stage.',
    ];
  };

  const saleDealForm = useMemo(
    () =>
      getFormValidation(
        fields(updateSalesTotalValue, orderValueRequiredForCertainStages)
      ),
    []
  );
  const [buttonText, setbuttonText] = useState<string>('Add');
  const [tittle, settittle] = useState<string>('');
  const getField = (key: string) => saleDealForm.$(key);

  useEffect(() => {
    loadCommonData();
    switch (mode) {
      case VIEW_MODE.ADD:
        {
          settittle(`Add Sales Deal - ${companyOverview.companyName}`);
        }
        break;
      case VIEW_MODE.EDIT:
        {
          setbuttonText('Update');
          settittle(`Edit Sales Deal - ${companyOverview.companyName}`);
        }
        break;
    }
  }, []);

  useEffect(() => {
    if (saleDeal && mode == VIEW_MODE.EDIT) {
      saleDealForm.set(saleDeal);

      if (saleDeal.valueOfTesting == 0)
        saleDealForm.set({ valueOfTesting: '0' });

      if (saleDeal.valueOfTraining == 0)
        saleDealForm.set({ valueOfTraining: '0' });

      if (saleDeal.valueOfPolicyReview == 0)
        saleDealForm.set({ valueOfPolicyReview: '0' });

      if (saleDeal.valueOfHealthTickServices == 0)
        saleDealForm.set({ valueOfHealthTickServices: '0' });

      if (
        saleDeal.valueOfPolicyReview == 0 &&
        saleDeal.valueOfTraining == 0 &&
        saleDeal.valueOfTesting == 0
      )
        saleDealForm.set({ totalValue: '0' });
    }
  }, [saleDeal]);

  const reload = () => {
    modal.close();
    modal?.close();
    refreshSalesGridList(companyRowID);
    getCompanyOverview(companyRowID);
  };

  const isReadOnly = () => {
    return (
      VIEW_MODE.EDIT == mode && DealStatus.ClosedLost == saleDeal.dealStatusID
    );
  };

  const [loading, setLoading] = useState<boolean>(false);

  const gridProps: IGridFormControlProps = {
    row: 1,
    col: 8,
    itemResponsive: {
      lg: 12,
      sm: 12,
      md: 4,
    },
    inputControls: [
      {
        field: getField('dealDescription'),
        fieldKey: 'dealDescription',
        type: EDITOR_TYPES.TEXT_FIELD,
        multiline: true,
        rows: 4,
        inputProps: {
          readOnly: isReadOnly(),
          maxLength: characterMaxLength,
          minLength: characterMinLength,
        },
        helperText: (
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Box sx={{ color: 'red' }}>
              {getField('dealDescription').value.length == characterMaxLength
                ? `Description cannot exceed ${characterMaxLength} words.`
                : ''}
            </Box>
            <span>
              {`${
                getField('dealDescription').value.length
              } / ${characterMaxLength}`}
            </span>
          </Box>
        ),
      },
      {
        field: getField('valueOfTesting'),
        fieldKey: 'valueOfTesting',
        type: EDITOR_TYPES.TEXT_FIELD,
        isMasked: true,
        inputProps: {
          readOnly: isReadOnly(),
        },
      },
      {
        field: getField('valueOfTraining'),
        fieldKey: 'valueOfTraining',
        type: EDITOR_TYPES.TEXT_FIELD,
        isMasked: true,
        inputProps: {
          readOnly: isReadOnly(),
        },
      },
      {
        field: getField('valueOfPolicyReview'),
        fieldKey: 'valueOfPolicyReview',
        type: EDITOR_TYPES.TEXT_FIELD,
        isMasked: true,
        inputProps: {
          readOnly: isReadOnly(),
        },
      },
      {
        field: getField('valueOfHealthTickServices'),
        fieldKey: 'valueOfHealthTickServices',
        type: EDITOR_TYPES.TEXT_FIELD,
        isMasked: true,
        inputProps: {
          readOnly: isReadOnly(),
        },
      },
      {
        field: getField('totalValue'),
        fieldKey: 'totalValue',
        type: EDITOR_TYPES.TEXT_FIELD,
        inputProps: {
          readOnly: true,
        },
        isMasked: true,
      },
      {
        field: getField('dealStatusID'),
        fieldKey: 'dealStatusID',
        type: EDITOR_TYPES.DROPDOWN,
        options: dealStatusList,
        inputProps: {
          readOnly: isReadOnly(),
        },
      },
    ],
    form: saleDealForm,
  };

  const onFromSubmit = () => {
    if (!saleDealForm.isValid) {
      saleDealForm.showErrors();
      return;
    }

    const request: ISalesDeal = { ...saleDealForm.values() };
    switch (mode) {
      case VIEW_MODE.ADD:
        request.companyRowID = companyRowID;
        if (
          request.dealStatusID === DealStatus.ClosedWonAndPostSaleFollowUp ||
          request.dealStatusID === DealStatus.ClosedLost
        ) {
          modal.open(
            <AddNoteConfirmation
              updateAction={(val) => {
                if (val.note != null || val.note.trim() != "") {
                  request.closureComment = val.note;
                  addSalesDeal(request);
                } else {
                  setLoading(false);
                }
              }}
              title={"Add Closure Comment"}
              additionalValidation="required|between:10,800"
              validationMessages={{
                required: "Comment is required.",
                between:
                  "Please enter a comment (10�800 characters) to confirm this status change.",
              }}
            />
          );
        } else {
          addSalesDeal(request);
        }
        break;
      case VIEW_MODE.EDIT:
        if (
          saleDeal.dealStatusID != request.dealStatusID &&
          (request.dealStatusID === DealStatus.ClosedWonAndPostSaleFollowUp ||
            request.dealStatusID === DealStatus.ClosedLost)
        ) {
          modal.open(
            <AddNoteConfirmation
              updateAction={(val) => {
                if (val.note != null || val.note.trim() != "") {
                  request.closureComment = val.note;
                  updateSalesDeal(request);
                }
              }}
              title={"Add Closure Comment"}
              label="Comment"
              additionalValidation="required|between:10,800"
              validationMessages={{
                required: "Comment is required.",
                between:
                  "Please enter a comment (10�800 characters) to confirm this status change.",
              }}
            />
          );
        } else {
          updateSalesDeal(request);
        }
        break;
    }
  };

  const updateSalesDeal = (request: ISalesDeal) => {
    setLoading(true);
    uiStore.setGlobalLoader(true);

    SalesDealService.updateSaleDeal(request)
      .pipe(
        finalize(() => {
          setLoading(false);
          uiStore.setGlobalLoader(false);
        })
      )
      .subscribe({
        next: (data: IResult<string>) => {
          ToastService.success(data.message);
          reload();
        },
        error: (error: AxiosError) => ToastService.axiosError(error),
      });
  };

  const addSalesDeal = (request: ISalesDeal) => {
    setLoading(true);
    uiStore.setGlobalLoader(true);

    SalesDealService.addSaleDeal(request)
      .pipe(
        finalize(() => {
          setLoading(false);
          uiStore.setGlobalLoader(false);
        })
      )
      .subscribe({
        next: (data: IResult<string>) => {
          ToastService.success(data.message);
          reload();
        },
        error: (error: AxiosError) => ToastService.axiosError(error),
      });
  };

  return (
    <ModalContent
      buttonText={buttonText}
      buttonDisabled={isReadOnly()}
      title={tittle}
      onSaveButtonClick={() => onFromSubmit()}
      btnLoading={loading}
    >
      <GridFormControl {...gridProps} />
    </ModalContent>
  );
};

export default observer(AddOrEditSaleDeal);
